import { typedKeys } from "@/utils/object-utils";
import { PREVIOUS_SUFFIX } from "../constants/previous";
import type {
  Attribute,
  AttributeGroup,
  AttributeGroups,
  AttributeId,
  FieldId,
  FieldValue,
  FieldValueType,
  SelfServiceTaskForm,
} from "../types";
import { createFieldId } from "./field-id";
import type { FormInitialValueProvider } from "./initial-value-provider";

/**
 * フォームからすべてのフィールド(Attribute)を抽出する
 * @param form フォーム定義
 * @returns 抽出された属性の配列
 */
export function getAllAttributes(form: SelfServiceTaskForm): Attribute[] {
  if (
    !form?.form ||
    !form.form.attributeCollections ||
    form.form.attributeCollections.length === 0
  ) {
    return [];
  }

  return form.form.attributeCollections.flatMap(
    (collection) =>
      collection.attributeGroupSettings?.flatMap(
        (group) => group.attributes || [],
      ) || [],
  );
}

/**
 * 属性IDから属性設定を検索する
 * @param form フォーム定義
 * @param attributeId 検索する属性ID
 * @returns 見つかった属性または undefined
 */
export function findAttributeById(
  form: SelfServiceTaskForm,
  attributeId: string,
): Attribute | undefined {
  if (!form?.form) return undefined;
  return getAllAttributes(form).find((attr) => attr.id === attributeId);
}

/**
 * FieldIdから属性値を取得する
 * @param values フィールド値のリスト
 * @param fieldId 検索するフィールドID
 * @returns フィールドの値、存在しない場合は undefined
 */
export function getValueById(
  values: FieldValue[],
  fieldId: FieldId,
): FieldValueType | undefined {
  if (!values || !Array.isArray(values)) {
    return undefined;
  }
  const value = values.find((v) => v.fieldId === fieldId);
  return value?.value;
}

/**
 * グループの表示順序を取得する
 * @param group 属性グループ
 * @returns 表示順序の数値
 */
export function getGroupSortOrder(group: AttributeGroup): number {
  return group.displayOrder || 0;
}

/**
 * 属性の表示順序を取得する
 * @param attribute 属性
 * @returns 表示順序の数値
 */
export function getAttributeSortOrder(attribute: Attribute): number {
  return attribute.itemOrder || 0;
}

/**
 * グループを表示順序でソートする
 * @param groups ソートするグループのリスト
 * @returns ソート済みのグループリスト
 */
export function sortGroups(groups: AttributeGroups): AttributeGroups {
  if (!groups) return [];
  return [...groups].sort(
    (a, b) => getGroupSortOrder(a) - getGroupSortOrder(b),
  );
}

/**
 * 属性を表示順序でソートする
 * @param attributes ソートする属性のリスト
 * @returns ソート済みの属性リスト
 */
export function sortAttributes(attributes: Attribute[]): Attribute[] {
  if (!attributes) return [];
  return [...attributes].sort(
    (a, b) => getAttributeSortOrder(a) - getAttributeSortOrder(b),
  );
}

/**
 * フォームの初期値を作成する
 * @param form フォーム定義
 * @param initialValues API cacheなどServerStateから取得した初期値（優先される）
 * @returns 初期化されたフィールド値のリスト
 */
export function createInitialValues(
  form: SelfServiceTaskForm,
  initialValueProvider: FormInitialValueProvider,
): FieldValue[] {
  if (!form?.form) return [];

  try {
    const values: FieldValue[] = [];

    const sourceValues = initialValueProvider.getInitialValues();

    const formValueMap = parseFormValue(sourceValues);

    form.form.attributeCollections.forEach((collection) => {
      const collectionId = collection.id;
      const collectionCount = !collection.isDynamic
        ? 1
        : (formValueMap[collectionId]?.length ?? 1);

      [...new Array(collectionCount)].forEach((_, collectionIndex) => {
        collection.attributeGroupSettings?.forEach((group) => {
          const groupId = group.id;
          const groupCount = !group.isDynamic
            ? 1
            : (formValueMap[collectionId]?.[collectionIndex]?.[groupId]
                ?.length ?? 1);
          [...new Array(groupCount)].forEach((_, groupIndex) => {
            group.attributes?.forEach((attr) => {
              const fieldId = createFieldId({
                collectionId,
                collectionIndex: collectionIndex,
                groupId,
                groupIndex,
                attributeId: attr.id,
              });

              const formValue = sourceValues[fieldId] ?? undefined;

              values.push({
                fieldId,
                attributeId: attr.id,
                value: formValue,
              });

              // 末尾にPreviousが付いているAttributeがあれば、変更前の値なのでvaluesにも追加する
              const previousAttributeId = (attr.id +
                PREVIOUS_SUFFIX) as AttributeId;
              const previousFieldId = createFieldId({
                collectionId,
                collectionIndex: collectionIndex,
                groupId,
                groupIndex,
                attributeId: previousAttributeId,
              });

              const previousFormValue =
                sourceValues[previousFieldId] ?? undefined;

              if (previousFormValue) {
                values.push({
                  fieldId: previousFieldId,
                  attributeId: previousAttributeId,
                  value: previousFormValue,
                });
              }
            });
          });
        });
      });
    });

    return values;
  } catch (error) {
    console.error("フォームの初期値作成でエラーが発生しました", { error });
    return [];
  }
}

function parseFormValue(initialValues?: Record<FieldId, FieldValueType>) {
  if (!initialValues) return {};
  const map: {
    [collectionId: string]: {
      [groupId: string]: {
        [attributeId: string]: FieldValueType;
      }[];
    }[];
  } = {};
  typedKeys(initialValues).forEach((key) => {
    const [collectionId, collectionIndex, groupId, groupIndex, attributeId] =
      key.split(".");
    if (!map[collectionId]) {
      map[collectionId] = [];
    }
    if (!map[collectionId][Number(collectionIndex)]) {
      map[collectionId][Number(collectionIndex)] = {};
    }
    if (!map[collectionId][Number(collectionIndex)][groupId]) {
      map[collectionId][Number(collectionIndex)][groupId] = [];
    }
    if (
      !map[collectionId][Number(collectionIndex)][groupId][Number(groupIndex)]
    ) {
      map[collectionId][Number(collectionIndex)][groupId][Number(groupIndex)] =
        {};
    }

    map[collectionId][Number(collectionIndex)][groupId][Number(groupIndex)][
      attributeId
    ] = initialValues[key];
  });
  return map;
}
